import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, Plus, Tag as TagIcon, ArrowRight, BookOpen, Clock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Button, Badge, SearchInput, EmptyState, Avatar } from "@/components/ui-em";

export default function KnowledgeBase() {
  const [params, setParams] = useSearchParams();
  const initialQ = params.get("q") || "";
  const [q, setQ] = useState(initialQ);
  const [category, setCategory] = useState(params.get("category") || "all");
  const [tag, setTag] = useState(params.get("tag") || "");
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    api.get("/kb/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const url = "/kb/articles";
    const qs = { q: q || undefined, category: category !== "all" ? category : undefined, tag: tag || undefined };
    api
      .get(url, { params: qs })
      .then((r) => setArticles(r.data))
      .finally(() => setLoading(false));
  }, [q, category, tag]);

  useEffect(() => {
    const next = {};
    if (q) next.q = q;
    if (category !== "all") next.category = category;
    if (tag) next.tag = tag;
    setParams(next, { replace: true });
  }, [q, category, tag, setParams]);

  const allTags = useMemo(() => {
    const s = new Set();
    articles.forEach((a) => (a.tags || []).forEach((t) => s.add(t)));
    return Array.from(s).slice(0, 12);
  }, [articles]);

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t("dashboard.module")}
        title={t("kb.title")}
        description={t("kb.description")}
        actions={
          user?.role === "admin" ? (
            <Button as={Link} to="/knowledge-base/new" icon={Plus} size="lg" data-testid="new-article-btn">
              {t("kb.newArticle")}
            </Button>
          ) : null
        }
      />

      {/* Search */}
      <SearchInput
        value={q}
        onChange={setQ}
        placeholder={t("kb.searchPlaceholder")}
        size="lg"
        autoFocus
        data-testid="kb-search"
      />

      {/* Category pills */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-2 px-2">
        <button
          onClick={() => setCategory("all")}
          data-testid="cat-all"
          className={`shrink-0 px-4 py-1.5 rounded-full text-xs font-medium ${
            category === "all" ? "bg-gold text-ink-0" : "bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white"
          }`}
          style={{ transitionProperty: "background-color, color", transitionDuration: "180ms" }}
        >
          {t("common.all")}
        </button>
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            data-testid={`cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
            className={`shrink-0 px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
              category === c ? "bg-gold text-ink-0" : "bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white"
            }`}
            style={{ transitionProperty: "background-color, color", transitionDuration: "180ms" }}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Tag filter */}
      {allTags.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-zinc-500 flex-wrap">
          <TagIcon className="w-3.5 h-3.5" />
          <span className="uppercase tracking-widest">{t("kb.tagsLabel")}</span>
          {allTags.map((tg) => (
            <button
              key={tg}
              onClick={() => setTag(tag === tg ? "" : tg)}
              className={`px-2.5 py-1 rounded-md ${
                tag === tg ? "bg-gold/15 text-gold" : "bg-white/5 text-zinc-400 hover:bg-white/10"
              }`}
              style={{ transitionProperty: "background-color, color", transitionDuration: "160ms" }}
            >
              #{tg}
            </button>
          ))}
          {tag && (
            <button onClick={() => setTag("")} className="text-zinc-500 hover:text-white">
              {t("kb.clearTag")}
            </button>
          )}
        </div>
      )}

      {/* Articles list */}
      {loading ? (
        <div className="text-zinc-500 text-sm">{t("common.loading")}</div>
      ) : articles.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title={t("kb.noArticlesYet")}
          description={
            q || category !== "all" || tag
              ? t("kb.tryDifferent")
              : t("kb.noArticlesDesc")
          }
          actionLabel={user?.role === "admin" ? t("kb.createFirstArticle") : undefined}
          actionTo={user?.role === "admin" ? "/knowledge-base/new" : undefined}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {articles.map((a) => (
            <Link
              key={a.id}
              to={`/knowledge-base/${a.id}`}
              data-testid={`article-card-${a.id}`}
              className="em-card em-card-hover p-6 flex flex-col gap-4 group"
            >
              <div className="flex items-center justify-between">
                <Badge variant="gold" size="sm" uppercase>{a.category}</Badge>
                {a.status === "draft" && <Badge variant="warn" size="xs" uppercase>{t("kb.draftBadge")}</Badge>}
              </div>
              <h3 className="font-display text-xl font-medium tracking-tight text-white group-hover:text-gold leading-snug" style={{ transitionProperty: "color", transitionDuration: "180ms" }}>
                {a.title}
              </h3>
              <p className="text-sm text-zinc-400 line-clamp-3 flex-1 leading-relaxed">{a.content_text?.slice(0, 200)}</p>
              {a.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {a.tags.slice(0, 3).map((tg) => (
                    <span key={tg} className="text-[10px] uppercase tracking-wider text-zinc-500">#{tg}</span>
                  ))}
                </div>
              )}
              <div className="pt-4 border-t border-white/5 flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <Avatar name={a.author} size="xs" />
                  <div className="min-w-0">
                    <div className="text-xs text-zinc-400 truncate">{a.author}</div>
                    <div className="text-[10px] text-zinc-600 flex items-center gap-1">
                      {a.reading_time > 0 && (
                        <><Clock className="w-2.5 h-2.5" /><span>{t("kb.readingTime", { n: a.reading_time })}</span></>
                      )}
                    </div>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-gold group-hover:translate-x-0.5" style={{ transitionProperty: "color, transform", transitionDuration: "180ms" }} />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
